// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.
// Changed from hardcoded links → DB-driven navItems → now delegates to the
// `header` region's block stack when present, with the legacy hardcoded
// layout kept as a fallback if the region is missing or empty.

"use client";

import Link from "next/link";
import { useState } from "react";

interface NavTreeItem {
  id: number;
  label: string;
  href: string;
  isExternal: boolean;
  children: NavTreeItem[];
}

interface NavbarProps {
  navItems: NavTreeItem[];
}

/**
 * Fallback Navbar used only when the `header` region has NO blocks.
 * The public layout prefers the region-based renderer and only falls
 * through to this hard-coded layout as a safety net (e.g. if the
 * header region was accidentally emptied in admin).
 */
export function Navbar({ navItems }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<number | null>(null);

  const ctaHref =
    navItems.find((item) => item.label.toLowerCase() === "contact")?.href ||
    "/contact";

  return (
    <nav className="w-full bg-stone-100 border-b-[3px] border-black py-4 px-6 flex justify-between items-center sticky top-0 z-50">
      <Link href="/" className="flex items-center gap-3">
        <img
          src="https://ik.imagekit.io/n5btsbywg/opichi-com/opichi-logo_3tA4f9-o4.png"
          alt="Opichi"
          loading="lazy"
          decoding="async"
          className="h-8 w-auto"
        />
        <span className="text-xs uppercase tracking-[0.2em] text-stone-600">
          AI Consultancy
        </span>
      </Link>

      <div className="hidden md:flex gap-8 text-sm uppercase tracking-widest items-center">
        {navItems.map((item) =>
          item.children.length > 0 ? (
            <div key={item.id} className="relative group">
              <div className="flex items-center gap-2">
                <Link
                  href={item.href}
                  className="hover:text-stone-600 transition-colors"
                  {...(item.isExternal
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                >
                  {item.label}
                </Link>
                <button
                  type="button"
                  className="hover:text-stone-600 transition-colors"
                  aria-haspopup="menu"
                  aria-label={`Open ${item.label} menu`}
                >
                  <span className="text-xs">▾</span>
                </button>
              </div>

              <div className="absolute left-0 top-full pt-2 hidden group-hover:block group-focus-within:block z-50">
                <div className="min-w-[240px] brutal-border-sm bg-white p-2">
                  {item.children.map((child) => (
                    <Link
                      key={child.id}
                      href={child.href}
                      className="block px-3 py-2 hover:bg-stone-100 transition-colors"
                      {...(child.isExternal
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <Link
              key={item.id}
              href={item.href}
              className="hover:text-stone-600 transition-colors"
              {...(item.isExternal
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              {item.label}
            </Link>
          )
        )}
      </div>

      <Link
        href={ctaHref}
        className="hidden md:block brutal-border-sm bg-black text-white px-4 py-2 text-sm uppercase tracking-widest hover:bg-stone-800 transition-colors"
      >
        Book Now
      </Link>

      <button
        className="md:hidden text-2xl"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Toggle menu"
      >
        {mobileOpen ? "✕" : "☰"}
      </button>

      {mobileOpen && (
        <div className="absolute top-full left-0 w-full bg-stone-100 border-b-[3px] border-black flex flex-col gap-4 p-6 md:hidden z-50">
          {navItems.map((item) =>
            item.children.length > 0 ? (
              <div key={item.id}>
                <div className="flex items-center gap-2">
                  <Link
                    href={item.href}
                    className="text-sm uppercase tracking-widest"
                    onClick={() => setMobileOpen(false)}
                    {...(item.isExternal
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {item.label}
                  </Link>
                  <button
                    type="button"
                    className="text-sm uppercase tracking-widest"
                    onClick={() =>
                      setMobileSubmenuOpen(
                        mobileSubmenuOpen === item.id ? null : item.id
                      )
                    }
                    aria-label={`Toggle ${item.label} submenu`}
                  >
                    <span className="text-xs">▾</span>
                  </button>
                </div>
                {mobileSubmenuOpen === item.id && (
                  <div className="mt-2 pl-4 flex flex-col gap-2 text-sm uppercase tracking-widest">
                    {item.children.map((child) => (
                      <Link
                        key={child.id}
                        href={child.href}
                        onClick={() => setMobileOpen(false)}
                        {...(child.isExternal
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={item.id}
                href={item.href}
                className="text-sm uppercase tracking-widest"
                onClick={() => setMobileOpen(false)}
                {...(item.isExternal
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {item.label}
              </Link>
            )
          )}
          <Link
            href={ctaHref}
            className="brutal-border-sm bg-black text-white px-4 py-2 text-sm uppercase tracking-widest text-center"
            onClick={() => setMobileOpen(false)}
          >
            Book Now
          </Link>
        </div>
      )}
    </nav>
  );
}
